import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { format } from 'date-fns'

import DownloadIcon from '@mui/icons-material/Download'

import axios from '../../dataClient/dataClient'
import { theme } from '../../themes/theme'
import { getFileSize } from '../../util/strings'
import { handleDownloadResponse } from '../../util/downloadFile'
import { IconButton } from '../common/Button'
import { BoldSmallBodyText, MiniErrorText, BoldMedBodyText } from '../styles/Text'
import { FlexRow, FlexEndButtonErrorContainer } from '../styles/Containers'
import DeletePIRFileButton from './DeletePIRFileButton'

const FileInfo = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 15px 15px;

  cursor: ${props => (props.clickable ? 'pointer' : 'auto')};
  :hover {
    background: ${props => (props.clickable ? `${theme.colors.primaryLight}` : 'none')};
    border-radius: ${theme.borderRadius.div};
  }

  @media only screen and (max-width: 800px) {
    flex-wrap: wrap;
    gap: 5px;
  }
`

const FileNameContainer = styled.div`
  width: ${props => (props.type === 'signature' ? '100%' : '60%')};

  display: flex;
  gap: 15px;

  @media only screen and (max-width: 800px) {
    width: 100%;
    padding-right: 0;
  }
`

const Text = styled(BoldMedBodyText)`
  line-height: 20px;
  text-wrap: wrap;
  word-break: break-word;

  @media only screen and (max-width: 800px) {
    width: 100%;
  }
`

const SmallText = styled(BoldSmallBodyText)`
  text-wrap: wrap;
  word-break: break-word;

  @media only screen and (max-width: 800px) {
    width: 100%;
  }
`

const SizeDateText = styled(Text)`
  width: 20%;

  @media only screen and (max-width: 800px) {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    width: 100%;
  }
`

const SizeDateTextRight = styled(SizeDateText)`
  text-align: right;

  @media only screen and (max-width: 800px) {
    text-align: left;
  }
`

const ButtonColumn = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 50px;
`

const ErrorText = styled(MiniErrorText)`
  text-align: right;
`

function PIRFile({ file, pirId, onDelete = () => {}, buttonAction = 'delete', fileClickAction = 'download', type = 'response' }) {
  const [downloadError, setDownloadError] = useState(false)

  async function handleClick(name, pirId, action) {
    setDownloadError(false)

    if (type === 'signature') {
      axios
        .get(`/download/${pirId}/${encodeURIComponent(name)}`, { responseType: 'blob' })
        .then(response => handleDownloadResponse(response, name, action))
        .catch(e => {
          setDownloadError(true)
          console.error('An error occurred while fetching the document', name, e)
        })
    } else {
      try {
        const response = await axios.get(`/download/${pirId}/${encodeURIComponent(name)}`, { responseType: 'blob' })
        handleDownloadResponse(response, name, action)
      } catch (e) {
        setDownloadError(true)
        console.error('An error occurred while fetching the document', name, e)
      }
    }
  }

  return (
    <FlexRow gap="15px">
      <FileInfo clickable onClick={() => handleClick(file.name, pirId, fileClickAction)}>
        <FileNameContainer type={type}>{type === 'signature' ? <SmallText>{file.name}</SmallText> : <Text>{file.name}</Text>}</FileNameContainer>
        {type === 'response' && (
          <>
            <SizeDateText>{getFileSize(file.size)}</SizeDateText>
            <SizeDateTextRight>{format(new Date(file.lastModified), 'MMM dd, yyyy')}</SizeDateTextRight>
          </>
        )}
      </FileInfo>
      <ButtonColumn>
        {buttonAction === 'delete' && <DeletePIRFileButton pirId={pirId} name={file.name} onDelete={onDelete} />}
        {buttonAction === 'download' && (
          <FlexEndButtonErrorContainer gap="0">
            <IconButton
              aria-label={type === 'signature' ? 'Download signature file' : 'Download file'}
              onClick={() => handleClick(file.name, pirId, 'download')}>
              <DownloadIcon />
            </IconButton>
            {downloadError && <ErrorText role="alert">Error, try again.</ErrorText>}
          </FlexEndButtonErrorContainer>
        )}
      </ButtonColumn>
    </FlexRow>
  )
}

PIRFile.propTypes = {
  file: PropTypes.shape({
    name: PropTypes.string.isRequired,
    size: PropTypes.number,
    lastModified: PropTypes.string
  }).isRequired,
  pirId: PropTypes.string.isRequired,
  onDelete: PropTypes.func,
  fileClickAction: PropTypes.oneOf(['download', 'view']),
  buttonAction: PropTypes.oneOf(['delete', 'download']),
  type: PropTypes.oneOf(['response', 'signature'])
}

export default PIRFile
